// The authoring-time resolved preview (docs/09 `/scenarios`, docs/14 §10).
// The persona selection, the draft, and the result are all transient: the
// panel only ever POSTs the non-mutating `:preview` colon read, and nothing
// it shows is written back to the scenario.
import { useEffect, useMemo, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { docUrl } from "../../lib/docLinks";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { unicodeCaseFold } from "../../lib/unicodeCaseFold";
import {
  Badge,
  Button,
  HelpTip,
  Inline,
  InlineError,
  RouterLink,
  Select,
  Skeleton,
  Stack,
  Text,
} from "../../ui";
import { fetchAllScenarioRows } from "../ScenarioRows";
import {
  launchDiagnosis,
  orderValidationGroups,
  validationExampleMessage,
} from "../simulations/launchDiagnosis";
import { fetchAllVariablePersonas } from "../../lib/variableScopeOptions";
import {
  ResolvedCellView,
  type ResolvedCellSupplierMap,
} from "../variables/ResolvedCellView";

type Scenario = components["schemas"]["ScenarioResponse"];
type ScenarioPreviewRequest = components["schemas"]["ScenarioPreviewRequest"];
type ResolvedVariable = components["schemas"]["ResolvedVariableResponse"];
type Draft = components["schemas"]["ScenarioRevise"];

// Matches PREVIEW_DEBOUNCE_MS on the launch form: an author typing an
// instruction must not fire a request per keystroke.
const PREVIEW_DEBOUNCE_MS = 300;

// Byte-exact with VARIABLE_TOKEN in `src/lib/variableTokens.ts`, kept as a
// splitting pattern so the surrounding literal text renders unchanged.
const TOKEN_SPLIT = /(\{[A-Za-z0-9_][A-Za-z0-9_-]*\})/g;
const TOKEN_EXACT = /^\{([A-Za-z0-9_][A-Za-z0-9_-]*)\}$/;

export interface ScenarioPreviewPanelProps {
  declarationRevision?: string;
  declarationsRefreshing?: boolean;
  displayInstruction?: string;
  draft: Draft;
  scenario: Scenario;
}

function tokenSpans(text: string, variables: Record<string, ResolvedVariable>) {
  // Suppliers are matched case-insensitively and resolved values keep the
  // declaration's canonical spelling (docs/14 §10), so `{Business_Name}` must
  // highlight against a `business_name` result.
  const resolvedKeys = new Set(
    Object.keys(variables).map((key) => unicodeCaseFold(key)),
  );
  return text.split(TOKEN_SPLIT).map((part, index) => {
    const token = TOKEN_EXACT.exec(part)?.[1];
    if (token === undefined || !resolvedKeys.has(unicodeCaseFold(token))) {
      return part;
    }
    return (
      <Badge key={`${unicodeCaseFold(token)}-${String(index)}`} tone="accent">
        {part}
      </Badge>
    );
  });
}

export function ScenarioPreviewPanel({
  declarationRevision = "",
  declarationsRefreshing = false,
  displayInstruction,
  draft,
  scenario,
}: ScenarioPreviewPanelProps) {
  const [personaId, setPersonaId] = useState("");
  // `null` means "untouched", which resolves to the first row: launch always
  // iterates rows, so a rowless preview would flatter the author.
  const [chosenRowId, setChosenRowId] = useState<string | null>(null);
  const [request, setRequest] = useState<ScenarioPreviewRequest | null>(null);

  const personas = useQuery({
    queryKey: queryKeys.personas.allForAgent(scenario.agent_id),
    queryFn: ({ signal }) =>
      fetchAllVariablePersonas(scenario.agent_id, signal),
  });
  const rows = useQuery({
    // The same key the row editor uses, so both surfaces share one cache entry.
    queryKey: queryKeys.scenarioRows.list({ scenario_id: scenario.id }),
    queryFn: ({ signal }) => fetchAllScenarioRows(scenario.id, signal),
  });

  const rowOptions = rows.data ?? [];
  const rowId =
    chosenRowId !== null &&
    (chosenRowId === "" || rowOptions.some((row) => row.id === chosenRowId))
      ? chosenRowId
      : (rowOptions[0]?.id ?? "");
  const selectionReady =
    personas.isSuccess &&
    rows.isSuccess &&
    personas.data.some((persona) => persona.id === personaId);
  const body = useMemo<ScenarioPreviewRequest | null>(
    () =>
      !selectionReady
        ? null
        : {
            draft,
            preview_persona_id: personaId,
            scenario_row_id: rowId.length === 0 ? null : rowId,
          },
    [draft, personaId, rowId, selectionReady],
  );
  const fingerprint = body === null ? null : JSON.stringify(body);
  const bodyRef = useRef(body);
  bodyRef.current = body;

  useEffect(() => {
    if (fingerprint === null) {
      setRequest(null);
      return;
    }
    const handle = window.setTimeout(() => {
      setRequest(bodyRef.current);
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [fingerprint]);

  const preview = useQuery({
    enabled: request !== null && selectionReady && !declarationsRefreshing,
    queryKey: queryKeys.scenarios.preview(
      scenario.id,
      JSON.stringify({
        request,
        declarationRevision,
        personaRevision: personas.data?.find(
          (persona) => persona.id === request?.preview_persona_id,
        )?.current_revision.id,
        row: rowOptions.find((row) => row.id === request?.scenario_row_id),
      }),
    ),
    queryFn: async ({ signal }) => {
      if (request === null) throw new Error("A preview request is required.");
      const { data, error } = await apiClient.POST(
        "/v1/scenarios/{id}:preview",
        { body: request, params: { path: { id: scenario.id } }, signal },
      );
      if (error) throw new ApiRequestError(error);
      return data;
    },
    staleTime: 0,
  });

  const isResolving =
    body !== null &&
    (declarationsRefreshing ||
      preview.isFetching ||
      fingerprint !== JSON.stringify(request));
  const result =
    selectionReady && !isResolving && !preview.isError
      ? preview.data
      : undefined;
  const variables = result?.variables ?? {};
  const validationGroups = orderValidationGroups(result?.validation ?? []);
  const persona = (personas.data ?? []).find((item) => item.id === personaId);
  const suppliers: ResolvedCellSupplierMap = {
    ...(persona === undefined
      ? {}
      : { persona: { id: persona.id, name: persona.display_name } }),
    scenario: { id: scenario.id, name: scenario.display_name },
  };
  const instruction =
    displayInstruction ??
    (typeof draft.content.instruction === "string"
      ? draft.content.instruction
      : "");

  return (
    <Stack gap={3}>
      <Inline align="center" gap={2}>
        <Text variant="caps">Preview</Text>
        <HelpTip
          label="About variable preview"
          docHref={docUrl("guides/use-variables-in-simulations")}
          docLabel="Use variables in simulations"
        >
          {
            "Choose a Persona and optional Scenario row to check resolved values. This selection is only a preview and is not saved. Prompt instructions stay unchanged; resolved variables reach the caller as separate context. Row and launch overrides can change the final values."
          }
        </HelpTip>
      </Inline>
      <Select
        disabled={!personas.isSuccess || personas.data.length === 0}
        label="Preview persona"
        value={personaId}
        onChange={(event) => {
          setPersonaId(event.currentTarget.value);
        }}
      >
        <option value="">Select a persona</option>
        {(personas.data ?? []).map((option) => (
          <option key={option.id} value={option.id}>
            {option.display_name}
          </option>
        ))}
      </Select>
      <Select
        disabled={!rows.isSuccess || rowOptions.length === 0}
        description="Defaults to the first row."
        label="Scenario row"
        value={rowId}
        onChange={(event) => {
          setChosenRowId(event.currentTarget.value);
        }}
      >
        <option value="">No row</option>
        {rowOptions.map((option) => (
          <option key={option.id} value={option.id}>
            Row {option.sort_order}
          </option>
        ))}
      </Select>
      {personas.isPending || rows.isPending ? (
        <Skeleton
          label="Loading preview options"
          shape="detail-field"
          rows={2}
        />
      ) : null}
      {personas.isSuccess && personas.data.length === 0 ? (
        <Inline gap={2}>
          <Text variant="muted">Create a Persona to preview values.</Text>
          <RouterLink
            to="/personas"
            search={{ agent: scenario.agent_id, new: "persona" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Create persona
          </RouterLink>
        </Inline>
      ) : null}
      {personas.isError ? (
        <Inline gap={2}>
          <InlineError>
            {requestErrorDescription(
              personas.error,
              "Personas failed to load.",
            )}
          </InlineError>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void personas.refetch()}
          >
            Retry personas
          </Button>
        </Inline>
      ) : null}
      {rows.isError ? (
        <Inline gap={2}>
          <InlineError>
            {requestErrorDescription(
              rows.error,
              "Scenario rows failed to load.",
            )}
          </InlineError>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void rows.refetch()}
          >
            Retry rows
          </Button>
        </Inline>
      ) : null}
      {preview.isError ? (
        <Inline gap={2}>
          <InlineError>
            {requestErrorDescription(preview.error, "Preview failed.")}
          </InlineError>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void preview.refetch()}
          >
            Retry preview
          </Button>
        </Inline>
      ) : null}
      {isResolving ? (
        <Skeleton label="Resolving preview" shape="table-row" rows={2} />
      ) : null}
      {validationGroups.length > 0 ? (
        <Stack aria-label="Preview validation diagnosis" gap={1} role="group">
          {validationGroups.map((group) => (
            <Stack key={`${group.key}:${group.cause}`} gap={1}>
              {launchDiagnosis(group).map((message, index) =>
                index === 0 ? (
                  <InlineError key={message}>{message}</InlineError>
                ) : (
                  <Text key={message} variant="muted">
                    {message}
                  </Text>
                ),
              )}
              {group.examples.length > 0 ? (
                <Text variant="muted">
                  Examples: {validationExampleMessage(group)}
                </Text>
              ) : null}
            </Stack>
          ))}
        </Stack>
      ) : null}
      {result === undefined ? null : (
        <>
          <Text>{tokenSpans(instruction, variables)}</Text>
          <ResolvedCellView
            supplierLinkTarget="_blank"
            suppliers={suppliers}
            variables={variables}
          />
        </>
      )}
    </Stack>
  );
}
